"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FlexClient = void 0;
const anchor = __importStar(require("@project-serum/anchor"));
const web3_js_1 = require("@solana/web3.js");
const spl_token_1 = require("@solana/spl-token");
const types_1 = require("./types");
const program_1 = require("../common/program");
const constants_1 = require("../common/constants");
const flex_1 = require("./flex");
const utils = __importStar(require("../common/utils"));
const flexUtils = __importStar(require("./flex-utils"));
const instructions = __importStar(require("./instructions"));
class FlexClient {
    constructor(connection, wallet, opts) {
        this._tokenAccounts = [];
        this._optionPositions = [];
        this._comboOptionPositions = [];
        this._bids = [];
        this._provider = new anchor.AnchorProvider(connection, wallet, opts);
        this._program = flex_1.flex.program;
    }
    /**
     * Anchor Program instance.
     */
    get program() {
        return this._program;
    }
    get programId() {
        return this._program.programId;
    }
    /**
     * Anchor provider instance.
     */
    get provider() {
        return this._provider;
    }
    get publicKey() {
        return this._provider.wallet.publicKey;
    }
    get whitelistFeesAccountAddress() {
        return this._whitelistFeesAccountAddress;
    }
    /**
     * Collection of all tokenAccounts associated with the client
     */
    get tokenAccounts() {
        return this._tokenAccounts;
    }
    /**
     * Collection of all client positions on zeta-flex options
     */
    get optionPositions() {
        return this._optionPositions;
    }
    /**
     * Collection of all client positions on zeta-flex options
     */
    get comboOptionPositions() {
        return this._comboOptionPositions;
    }
    /**
     * Collection of all client bids on zeta-flex auctions
     */
    get bids() {
        return this._bids;
    }
    static async load(connection, wallet, opts = utils.defaultCommitment()) {
        if (!flex_1.flex.isInitialized) {
            throw Error("Flex program is not initialized");
        }
        let client = new FlexClient(connection, wallet, opts);
        let [whitelistFeesAccountAddress, _nonce] = flexUtils.getWhitelistFeesAccountAddress(flex_1.flex.programId, wallet.publicKey);
        client._whitelistFeesAccountAddress = whitelistFeesAccountAddress;
        await client.updateState();
        return client;
    }
    async initializeOption(underlyingMint, collateralMint, exerciseMint, settlementStart, settlementEnd, collateralAmount, strike, kind, settlementType, oracleMethod) {
        try {
            strike.toNumber();
        }
        catch (e) {
            throw Error("Strike is > 53 bits");
        }
        let underlying = flex_1.flex.getUnderlying(underlyingMint);
        let [optionAccount, _optionAccountNonce] = flexUtils.getOptionAccountAddress(flex_1.flex.program.programId, underlying.address, new anchor.BN(underlying.count));
        let [vault, _vaultNonce] = flexUtils.getVaultAddress(flex_1.flex.program.programId, optionAccount);
        let [optionMint, _optionMintNonce] = flexUtils.getOptionMintAddress(flex_1.flex.program.programId, optionAccount);
        let [userOptionTokenAccount, _userOptionTokenAccountNonce] = flexUtils.getUserOptionMintTokenAccountAddress(flex_1.flex.program.programId, optionMint, this.publicKey);
        let feeRecipientCollateralTokenAccount = await (0, spl_token_1.getAssociatedTokenAddress)(collateralMint, flex_1.flex.state.feeRecipient);
        let userCollateralTokenAddress;
        let userExerciseTokenAddress;
        let remainingAccounts = [];
        let collateralKeypair;
        let exerciseKeypair;
        let preIxs = [];
        let postIxs = [];
        remainingAccounts.push({
            pubkey: this._whitelistFeesAccountAddress,
            isSigner: false,
            isWritable: false,
        }, {
            pubkey: this.publicKey,
            isSigner: true,
            isWritable: false,
        });
        let amountNeeded = collateralAmount.toNumber();
        try {
            await flex_1.flex.program.account.whitelistFeesAccount.fetch(this._whitelistFeesAccountAddress);
        }
        catch (e) {
            amountNeeded += flexUtils.calculateMintingFees(collateralAmount.toNumber());
        }
        [preIxs, postIxs, collateralKeypair, userCollateralTokenAddress] =
            await utils.createTokenAccountInstructions(this._provider.connection, collateralMint, this.publicKey, this.publicKey, amountNeeded, true);
        if (settlementType == types_1.SettlementType.PHYSICAL) {
            let [_preIxs, _postIxs, _exerciseKeypair, _userExerciseTokenAddress] = await utils.createTokenAccountInstructions(this._provider.connection, exerciseMint, this.publicKey, this.publicKey, 0, false);
            preIxs.push(..._preIxs);
            postIxs.push(..._postIxs);
            exerciseKeypair = _exerciseKeypair;
            userExerciseTokenAddress = _userExerciseTokenAddress;
            remainingAccounts.push({
                pubkey: exerciseMint,
                isSigner: false,
                isWritable: false,
            }, {
                pubkey: userExerciseTokenAddress,
                isSigner: false,
                isWritable: false,
            });
        }
        try {
            await (0, spl_token_1.getAccount)(this.provider.connection, feeRecipientCollateralTokenAccount);
        }
        catch (e) {
            preIxs.push((0, spl_token_1.createAssociatedTokenAccountInstruction)(this.publicKey, feeRecipientCollateralTokenAccount, flex_1.flex.state.feeRecipient, collateralMint));
        }
        let initOptionArgs = {
            collateralAmount,
            settlementStart,
            settlementEnd,
            strike,
            kind: (0, types_1.toProgramKind)(kind),
            settlementType: (0, types_1.toProgramSettlementType)(settlementType),
            oracleMethod: (0, types_1.toProgramOracleMethod)(oracleMethod),
        };
        let tx = utils.combineIxsForTx(...preIxs, await instructions.initializeOptionV2Ix(initOptionArgs, underlying.address, vault, collateralMint, userCollateralTokenAddress, this.publicKey, optionAccount, optionMint, userOptionTokenAccount, feeRecipientCollateralTokenAccount, remainingAccounts), ...postIxs);
        return await utils.processTransaction(program_1.Program.FLEX, this._provider, tx, [
            collateralKeypair,
            exerciseKeypair,
        ]);
    }
    async initializeComboOption(underlyingMint, collateralMint, settlementStart, settlementEnd, optionParams, settlementType, oracleMethod) {
        let underlying = flex_1.flex.getUnderlying(underlyingMint);
        let [optionAccount, _optionAccountNonce] = flexUtils.getOptionAccountAddress(flex_1.flex.program.programId, underlying.address, new anchor.BN(underlying.count));
        let [vault, _vaultNonce] = flexUtils.getVaultAddress(flex_1.flex.program.programId, optionAccount);
        let [optionMint, _optionMintNonce] = flexUtils.getOptionMintAddress(flex_1.flex.program.programId, optionAccount);
        let [userOptionTokenAccount, _userOptionTokenAccountNonce] = flexUtils.getUserOptionMintTokenAccountAddress(flex_1.flex.program.programId, optionMint, this.publicKey);
        let feeRecipientCollateralTokenAccount = await (0, spl_token_1.getAssociatedTokenAddress)(collateralMint, flex_1.flex.state.feeRecipient);
        let preIxs = [];
        let postIxs = [];
        let remainingAccounts = [];
        remainingAccounts.push({
            pubkey: this._whitelistFeesAccountAddress,
            isSigner: false,
            isWritable: false,
        }, {
            pubkey: this.publicKey,
            isSigner: true,
            isWritable: false,
        });
        let collateralKeypair = undefined;
        let userCollateralTokenAddress = undefined;
        [preIxs, postIxs, collateralKeypair, userCollateralTokenAddress] =
            await utils.createTokenAccountInstructions(this._provider.connection, collateralMint, this.publicKey, this.publicKey, flexUtils.getCollateralRequiredForComboOption(optionParams), true);
        try {
            await (0, spl_token_1.getAccount)(this.provider.connection, feeRecipientCollateralTokenAccount);
        }
        catch (e) {
            preIxs.push((0, spl_token_1.createAssociatedTokenAccountInstruction)(this.publicKey, feeRecipientCollateralTokenAccount, flex_1.flex.state.feeRecipient, collateralMint));
        }
        let programOptionParams = (0, types_1.convertToProgramComboOptionParams)(optionParams);
        let initComboOptionArgs = {
            settlementStart,
            settlementEnd,
            optionParams: programOptionParams,
            settlementType: (0, types_1.toProgramSettlementType)(settlementType),
            oracleMethod: (0, types_1.toProgramOracleMethod)(oracleMethod),
        };
        let tx = utils.combineIxsForTx(...preIxs, await instructions.initializeComboOptionV2Ix(initComboOptionArgs, underlying.address, vault, collateralMint, userCollateralTokenAddress, this.publicKey, optionAccount, optionMint, userOptionTokenAccount, feeRecipientCollateralTokenAccount, remainingAccounts), ...postIxs);
        return await utils.processTransaction(program_1.Program.FLEX, this._provider, tx, [
            collateralKeypair,
        ]);
    }
    async burnOption(burnAmount, optionAccount, optionType) {
        await this.updateTokenAccounts();
        let optionInfo = flex_1.flex.getOption(optionAccount, optionType);
        let userCollateralTokenAccounts = this.getTokenAccountsByMint(optionInfo.collateralMint);
        let userCollateralTokenAddress = undefined;
        let collateralKeypair;
        let preIxs = [];
        let postIxs = [];
        if (userCollateralTokenAccounts.length == 0) {
            console.log("Client has no collateral token account for this option! Creating now...");
            [preIxs, postIxs, collateralKeypair, userCollateralTokenAddress] =
                await utils.createTokenAccountInstructions(this._provider.connection, optionInfo.collateralMint, this.publicKey, this.publicKey, 0, true);
        }
        else {
            userCollateralTokenAddress = userCollateralTokenAccounts[0].address;
        }
        let userOptionTokenAccounts = this.getTokenAccountsByMint(optionInfo.optionMint);
        if (userOptionTokenAccounts.length == 0) {
            throw Error("User attempted to burn options they don't have");
        }
        let tx = utils.combineIxsForTx(...preIxs, await instructions.burnOptionIx(burnAmount, optionInfo, userCollateralTokenAddress, userOptionTokenAccounts[0].address, this.publicKey), ...postIxs);
        return await utils.processTransaction(program_1.Program.FLEX, this._provider, tx, [
            collateralKeypair,
        ]);
    }
    async collectRemainingCollateral(optionAccount, optionType) {
        await this.updateTokenAccounts();
        let optionInfo = flex_1.flex.getOption(optionAccount, optionType);
        let userCollateralTokenAccounts = this.getTokenAccountsByMint(optionInfo.collateralMint);
        let userCollateralTokenAddress;
        let collateralKeypair;
        let preIxs = [];
        let postIxs = [];
        if (userCollateralTokenAccounts.length == 0) {
            console.log("Client has no collateral token account for this option! Creating now...");
            [preIxs, postIxs, collateralKeypair, userCollateralTokenAddress] =
                await utils.createTokenAccountInstructions(this._provider.connection, optionInfo.collateralMint, this.publicKey, this.publicKey, 0, true);
        }
        else {
            userCollateralTokenAddress = userCollateralTokenAccounts[0].address;
        }
        let userOptionTokenAccounts = this.getTokenAccountsByMint(optionInfo.optionMint);
        if (userOptionTokenAccounts.length == 0) {
            console.log("User has no more option tokens");
            let [tokenAccAddress, _nonce] = flexUtils.getUserOptionMintTokenAccountAddress(flex_1.flex.programId, optionInfo.optionMint, this.publicKey);
            userOptionTokenAccounts.push({
                address: tokenAccAddress,
                mint: optionInfo.optionMint,
                amount: BigInt(0),
            });
        }
        let midIxs = await Promise.all(userOptionTokenAccounts.map(async (optionTokenAccount) => {
            return await instructions.collectRemainingCollateralIx(optionInfo, userCollateralTokenAddress, optionTokenAccount.address, this.publicKey);
        }));
        let tx = utils.combineIxsForTx(...preIxs, ...midIxs, ...postIxs);
        return await utils.processTransaction(program_1.Program.FLEX, this._provider, tx, [
            collateralKeypair,
        ]);
    }
    async initializeAuction(auctionTokenMint, bidCurrencyTokenMint, auctionAmount, bidEnd, cooldownEnd) {
        let [auctionAccount, _auctionNonce] = flexUtils.getAuctionAddress(flex_1.flex.programId, auctionTokenMint, this.publicKey, auctionAmount, bidEnd, cooldownEnd);
        let [auctionVault, _auctionVaultNonce] = flexUtils.getAuctionVaultAddress(flex_1.flex.programId, auctionTokenMint, this.publicKey, auctionAmount, bidEnd, cooldownEnd);
        let userAuctionTokenAccounts = this.getTokenAccountsByMint(auctionTokenMint);
        if (userAuctionTokenAccounts.length == 0) {
            throw Error("User has no auction mint token account");
        }
        let initAuctionArgs = {
            amount: auctionAmount,
            bidEnd,
            cooldownEnd,
        };
        let tx = new web3_js_1.Transaction();
        tx.add(await instructions.initializeAuctionIx(initAuctionArgs, auctionAccount, this.publicKey, auctionVault, auctionTokenMint, userAuctionTokenAccounts[0].address, bidCurrencyTokenMint));
        return await utils.processTransaction(program_1.Program.FLEX, this._provider, tx);
    }
    async acceptBid(bidAccount) {
        let bidAccountInfo = flex_1.flex.getBidByKey(bidAccount);
        if (bidAccountInfo == null) {
            throw Error("Bid account not found!");
        }
        let auctionAccountInfo = flex_1.flex.getAuctionByKey(bidAccountInfo.auctionAccount);
        if (auctionAccountInfo == null) {
            throw Error("Auction account not found!");
        }
        let [preIxs, postIxs, bidKeypair, userBidCurrencyTokenAccountAddress] = await utils.createTokenAccountInstructions(this._provider.connection, bidAccountInfo.bidCurrencyMint, this.publicKey, this.publicKey, 0, true);
        // Fine to ignore these ixs We don't support auctions for SOL
        let [_preIxs, _postIxs, _keypair, bidderAuctionTokenAccountAddress] = await utils.createTokenAccountInstructions(this._provider.connection, auctionAccountInfo.auctionTokenMint, bidAccountInfo.bidder, this.publicKey, 0, true);
        preIxs.push(..._preIxs);
        let tx = utils.combineIxsForTx(...preIxs, await instructions.acceptBidIx(this.publicKey, bidAccountInfo, auctionAccountInfo, bidderAuctionTokenAccountAddress, userBidCurrencyTokenAccountAddress), ...postIxs);
        return await utils.processTransaction(program_1.Program.FLEX, this._provider, tx, [
            bidKeypair,
        ]);
    }
    async cancelAuction(auctionAccount) {
        let auctionAccountInfo = flex_1.flex.getAuctionByKey(auctionAccount);
        if (auctionAccountInfo == null) {
            throw Error("Auction account not found!");
        }
        let userAuctionTokenAccounts = this.getTokenAccountsByMint(auctionAccountInfo.auctionTokenMint);
        let userAuctionTokenAccount = undefined;
        let preIxs = [];
        let postIxs = [];
        if (userAuctionTokenAccounts.length == 0) {
            userAuctionTokenAccount = await (0, spl_token_1.getAssociatedTokenAddress)(auctionAccountInfo.auctionTokenMint, this.publicKey);
            preIxs.push((0, spl_token_1.createAssociatedTokenAccountInstruction)(this.publicKey, userAuctionTokenAccount, this.publicKey, auctionAccountInfo.auctionTokenMint));
        }
        else {
            userAuctionTokenAccount = userAuctionTokenAccounts[0].address;
        }
        let tx = utils.combineIxsForTx(...preIxs, await instructions.cancelAuctionIx(this.publicKey, auctionAccountInfo, userAuctionTokenAccount), ...postIxs);
        return await utils.processTransaction(program_1.Program.FLEX, this._provider, tx);
    }
    async bidAuction(amount, auctionAccount, bidCurrencyMint) {
        let auctionAccountInfo = flex_1.flex.getAuctionByKey(auctionAccount);
        if (auctionAccountInfo == null) {
            throw Error("Auction account not found!");
        }
        let [bidAccountAddress, _bidAccountNonce] = flexUtils.getBidAccountAddress(flex_1.flex.program.programId, auctionAccount, this.publicKey);
        let [bidVaultAddress, _bidVaultNonce] = flexUtils.getBidVaultAddress(flex_1.flex.program.programId, auctionAccount, this.publicKey);
        if (auctionAccountInfo.bidCurrencyMint.equals(web3_js_1.PublicKey.default) &&
            !bidCurrencyMint) {
            throw Error("Must specify bid currency mint to bid on auction with any bid currency");
        }
        let bidCurrencyMintAddress = bidCurrencyMint
            ? bidCurrencyMint
            : auctionAccountInfo.bidCurrencyMint;
        let [preIxs, postIxs, bidKeypair, bidderBidCurrencyTokenAccountAddress] = await utils.createTokenAccountInstructions(this._provider.connection, bidCurrencyMintAddress, this.publicKey, this.publicKey, amount, true);
        // Fine to ignore these ixs We don't support auctions for SOL
        let [_preIxs, _postIxs, _keypair, _userAuctionTokenAccountAddress] = await utils.createTokenAccountInstructions(this._provider.connection, auctionAccountInfo.auctionTokenMint, this.publicKey, this.publicKey, 0, true);
        preIxs.push(..._preIxs);
        let tx = utils.combineIxsForTx(...preIxs, await instructions.bidAuctionIx(auctionAccount, bidAccountAddress, bidVaultAddress, this.publicKey, bidCurrencyMintAddress, bidderBidCurrencyTokenAccountAddress, amount), ...postIxs);
        return await utils.processTransaction(program_1.Program.FLEX, this._provider, tx, [
            bidKeypair,
        ]);
    }
    async cancelBid(bidAccount) {
        let bidToCancel = undefined;
        this._bids.forEach((bid) => {
            if (bid.address.equals(bidAccount)) {
                bidToCancel = bid;
            }
        });
        if (bidToCancel == undefined) {
            throw Error("Client attempted to cancel non-existent bid.");
        }
        let bidVaultInfo = await (0, spl_token_1.getAccount)(this._provider.connection, bidToCancel.bidVault);
        let [preIxs, postIxs, bidKeypair, bidderBidCurrencyTokenAccountAddress] = await utils.createTokenAccountInstructions(this._provider.connection, bidVaultInfo.mint, this.publicKey, this.publicKey, 0, true);
        let tx = utils.combineIxsForTx(...preIxs, await instructions.cancelBidIx(bidToCancel.auctionAccount, this.publicKey, bidAccount, bidToCancel.bidVault, bidderBidCurrencyTokenAccountAddress), ...postIxs);
        return await utils.processTransaction(program_1.Program.FLEX, this._provider, tx, [
            bidKeypair,
        ]);
    }
    async exerciseOption(optionAccount, exerciseAmount) {
        await this.updateTokenAccounts();
        let optionAccountInfo = flex_1.flex.getOption(optionAccount, types_1.FlexOptionType.Normal);
        let userCollateralTokenAccount = undefined;
        let userExerciseTokenAccount = undefined;
        let userOptionTokenAccounts = [];
        let preIxs = [];
        let postIxs = [];
        this._tokenAccounts.forEach((tokenAcc) => {
            if (tokenAcc.mint.equals(optionAccountInfo.collateralMint)) {
                userCollateralTokenAccount = tokenAcc.address;
            }
            if (tokenAcc.mint.equals(optionAccountInfo.optionMint)) {
                userOptionTokenAccounts.push(tokenAcc.address);
            }
            if (tokenAcc.mint.equals(optionAccountInfo.exerciseMint)) {
                userExerciseTokenAccount = tokenAcc.address;
            }
        });
        if (userOptionTokenAccounts.length == 0) {
            throw Error("Client attempted to exercise options that they don't have.");
        }
        let collateralKeypair;
        if (userCollateralTokenAccount === undefined) {
            console.log("Client has no collateral token account for this option! Creating now...");
            [preIxs, postIxs, collateralKeypair, userCollateralTokenAccount] =
                await utils.createTokenAccountInstructions(this._provider.connection, optionAccountInfo.collateralMint, this.publicKey, this.publicKey, 0, true);
        }
        else {
            if (optionAccountInfo.collateralMint.equals(constants_1.SOL_PUBKEY)) {
                postIxs.push((0, spl_token_1.createCloseAccountInstruction)(userCollateralTokenAccount, this.publicKey, this.publicKey));
            }
        }
        let remainingAccounts = [];
        let exerciseKeypair;
        if (optionAccountInfo.settlementType == types_1.SettlementType.PHYSICAL) {
            let wrappedNativeTokensNeeded = await flexUtils.getTokenAmountToExercise(this.provider.connection, optionAccountInfo, this._tokenAccounts, exerciseAmount);
            let [_preIxs, _postIxs, _exerciseKeypair, _userExerciseTokenAccount] = await utils.createTokenAccountInstructions(this._provider.connection, optionAccountInfo.exerciseMint, this.publicKey, this.publicKey, wrappedNativeTokensNeeded, true);
            preIxs.push(..._preIxs);
            postIxs.push(..._postIxs);
            exerciseKeypair = _exerciseKeypair;
            userExerciseTokenAccount = _userExerciseTokenAccount;
            remainingAccounts.push({
                pubkey: userExerciseTokenAccount,
                isSigner: false,
                isWritable: true,
            }, {
                pubkey: optionAccountInfo.creatorExerciseMintTokenAccount,
                isSigner: false,
                isWritable: true,
            }, {
                pubkey: optionAccountInfo.exerciseMint,
                isSigner: false,
                isWritable: false,
            });
        }
        let midIxs = await Promise.all(userOptionTokenAccounts.map(async (optionTokenAcc) => {
            return await instructions.exerciseOptionIx(optionAccountInfo, userCollateralTokenAccount, optionTokenAcc, this.publicKey, remainingAccounts, exerciseAmount);
        }));
        let tx = utils.combineIxsForTx(...preIxs, ...midIxs, ...postIxs);
        let txSig = await utils.processTransaction(program_1.Program.FLEX, this._provider, tx, [collateralKeypair, exerciseKeypair]);
        await this.updateState();
        return txSig;
    }
    async exerciseComboOption(optionAccount, exerciseAmount) {
        await this.updateTokenAccounts();
        let comboOptionAccountInfo = flex_1.flex.getOption(optionAccount, types_1.FlexOptionType.Combo);
        let userCollateralTokenAccount = undefined;
        let userOptionTokenAccounts = [];
        let preIxs = [];
        let postIxs = [];
        this._tokenAccounts.forEach((tokenAcc) => {
            if (tokenAcc.mint.equals(comboOptionAccountInfo.collateralMint)) {
                userCollateralTokenAccount = tokenAcc.address;
            }
            if (tokenAcc.mint.equals(comboOptionAccountInfo.optionMint)) {
                userOptionTokenAccounts.push(tokenAcc.address);
            }
        });
        if (userOptionTokenAccounts.length == 0) {
            throw Error("Client attempted to exercise options that they don't have.");
        }
        let collateralKeypair;
        if (userCollateralTokenAccount === undefined) {
            console.log("Client has no collateral token account for this option! Creating now...");
            [preIxs, postIxs, collateralKeypair, userCollateralTokenAccount] =
                await utils.createTokenAccountInstructions(this._provider.connection, comboOptionAccountInfo.collateralMint, this.publicKey, this.publicKey, 0, true);
        }
        else {
            if (comboOptionAccountInfo.collateralMint.equals(constants_1.SOL_PUBKEY)) {
                postIxs.push((0, spl_token_1.createCloseAccountInstruction)(userCollateralTokenAccount, this.publicKey, this.publicKey));
            }
        }
        let midIxs = await Promise.all(userOptionTokenAccounts.map(async (optionTokenAcc) => {
            return await instructions.exerciseOptionIx(comboOptionAccountInfo, userCollateralTokenAccount, optionTokenAcc, this.publicKey, [], exerciseAmount);
        }));
        let tx = utils.combineIxsForTx(...preIxs, ...midIxs, ...postIxs);
        let txSig = await utils.processTransaction(program_1.Program.FLEX, this._provider, tx, [collateralKeypair]);
        await this.updateState();
        return txSig;
    }
    updatePositions() {
        let optionPositions = [];
        let comboOptionPositions = [];
        this._tokenAccounts.forEach((tokenAcc) => {
            flex_1.flex.options.forEach((option) => {
                if (tokenAcc.mint.equals(option.optionMint) &&
                    Number(tokenAcc.amount) != 0) {
                    optionPositions.push({
                        optionAccount: option.optionAccount,
                        amount: Number(tokenAcc.amount),
                        strike: option.strike,
                        expiry: option.expiry,
                    });
                }
            });
            flex_1.flex.comboOptions.forEach((comboOption) => {
                if (tokenAcc.mint.equals(comboOption.optionMint) &&
                    Number(tokenAcc.amount) != 0) {
                    comboOptionPositions.push({
                        comboOptionAccount: comboOption.optionAccount,
                        amount: Number(tokenAcc.amount),
                        params: comboOption.optionParams,
                        expiry: comboOption.expiry,
                    });
                }
            });
        });
        this._optionPositions = optionPositions;
        this._comboOptionPositions = comboOptionPositions;
    }
    getExpiredPositions() {
        let expiredOptionPositions = [];
        for (var i = 0; i < this._optionPositions.length; i++) {
            if (this._optionPositions[i].expiry < flex_1.flex.clockTimestamp) {
                expiredOptionPositions.push(this._optionPositions[i]);
            }
        }
        let expiredComboOptionPositions = [];
        for (var i = 0; i < this._comboOptionPositions.length; i++) {
            if (this._comboOptionPositions[i].expiry < flex_1.flex.clockTimestamp) {
                expiredComboOptionPositions.push(this._comboOptionPositions[i]);
            }
        }
        return [expiredOptionPositions, expiredComboOptionPositions];
    }
    getLivePositions() {
        let liveOptionPositions = [];
        for (var i = 0; i < this._optionPositions.length; i++) {
            if (this._optionPositions[i].expiry > flex_1.flex.clockTimestamp) {
                liveOptionPositions.push(this._optionPositions[i]);
            }
        }
        let liveComboOptionPositions = [];
        for (var i = 0; i < this._comboOptionPositions.length; i++) {
            if (this._comboOptionPositions[i].expiry > flex_1.flex.clockTimestamp) {
                liveComboOptionPositions.push(this._comboOptionPositions[i]);
            }
        }
        return [liveOptionPositions, liveComboOptionPositions];
    }
    updateBids() {
        if (flex_1.flex.bids != undefined) {
            this._bids = flex_1.flex.bids.filter((bid) => bid.bidder.equals(this.publicKey));
        }
        for (var i = 0; i < this._bids.length; i++) {
            let bid = this._bids[i];
            const auction = flex_1.flex.auctions.find((a) => a.address.equals(bid.auctionAccount));
            if (!auction) {
                let auctionAccountInfo = flex_1.flex.getAuctionByKey(bid.auctionAccount);
                if (auctionAccountInfo == null) {
                    continue;
                }
                let optionInfo = flex_1.flex.getOptionByMint(auctionAccountInfo.auctionTokenMint, types_1.FlexOptionType.Normal);
                if (optionInfo != null) {
                    bid.optionAccount = optionInfo.optionAccount;
                }
                let comboOptionInfo = flex_1.flex.getOptionByMint(auctionAccountInfo.auctionTokenMint, types_1.FlexOptionType.Combo);
                if (comboOptionInfo != null) {
                    bid.optionAccount = comboOptionInfo.optionAccount;
                }
            }
            else if (flex_1.flex.getOptionByMint(auction.auctionTokenMint, types_1.FlexOptionType.Normal) !=
                null) {
                bid.optionAccount = flex_1.flex.getOptionByMint(auction.auctionTokenMint, types_1.FlexOptionType.Normal).optionAccount;
            }
            else if (flex_1.flex.getOptionByMint(auction.auctionTokenMint, types_1.FlexOptionType.Combo) !=
                null) {
                bid.optionAccount = flex_1.flex.getOptionByMint(auction.auctionTokenMint, types_1.FlexOptionType.Combo).optionAccount;
            }
        }
    }
    getCancellableBids() {
        let cancellableBids = [];
        for (var i = 0; i < this._bids.length; i++) {
            if (flex_1.flex.clockTimestamp < this._bids[i].bidEnd ||
                flex_1.flex.clockTimestamp > this._bids[i].cooldownEnd) {
                cancellableBids.push(this._bids[i]);
            }
        }
        return cancellableBids;
    }
    getAuctionBid(auctionAccount) {
        for (var i = 0; i < this._bids.length; i++) {
            if (this._bids[i].auctionAccount.equals(auctionAccount)) {
                return this._bids[i];
            }
        }
        return null;
    }
    async updateTokenAccounts() {
        const accounts = await this._provider.connection.getTokenAccountsByOwner(this.publicKey, {
            programId: spl_token_1.TOKEN_PROGRAM_ID,
        });
        let tokenAccounts = [];
        for (var i = 0; i < accounts.value.length; i++) {
            let account = accounts.value[i].account;
            const accountInfo = spl_token_1.AccountLayout.decode(account.data);
            tokenAccounts.push({
                address: accounts.value[i].pubkey,
                mint: new web3_js_1.PublicKey(accountInfo.mint),
                // TODO: check later - should store as BN?
                amount: accountInfo.amount,
            });
        }
        this._tokenAccounts = tokenAccounts;
    }
    getTokenAccountsByMint(mint) {
        let tokenAccounts = [];
        for (var i = 0; i < this._tokenAccounts.length; i++) {
            if (this._tokenAccounts[i].mint.equals(mint)) {
                tokenAccounts.push(this._tokenAccounts[i]);
            }
        }
        return tokenAccounts;
    }
    async updateState() {
        await flex_1.flex.updateState();
        this.updateBids();
        await this.updateTokenAccounts();
        this.updatePositions();
    }
}
exports.FlexClient = FlexClient;
